// Central place for the handful of deployment constants this app needs.
// Nothing here is a secret — client_id is public by OIDC design, and the
// GeoServer endpoints are already public per docs/deployment.md.

/** Authentik OIDC client for this app. Registered per docs/deployment.md
 * "Maps OIDC client (maps)" — same Confidential + Authorization Code +
 * Refresh Token pattern as hub/erp/farm. Placeholder client_id until the
 * real one is registered in Authentik (see that doc section for the
 * manual registration steps this repo cannot perform itself). */
export const AUTHENTIK_CLIENT_ID = import.meta.env.VITE_AUTHENTIK_CLIENT_ID || 'REPLACE_WITH_MAPS_CLIENT_ID';

/** Where Authentik should send the browser after a successful login. */
export const REDIRECT_URI = import.meta.env.VITE_REDIRECT_URI || `${window.location.origin}/callback`;

/** Authentik's own hosted authorize endpoint — same shape as design/hub/login.html's
 * data-authorize-url, but for the `maps` client instead of `hub`. */
export function authorizeUrl(): string {
  const params = new URLSearchParams({
    client_id: AUTHENTIK_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    scope: 'openid email profile',
  });
  return `https://auth.abrarfarms.com/application/o/authorize/?${params.toString()}`;
}

/** GeoServer OGC endpoints, per docs/deployment.md's "Geospatial stack" section.
 * Public, CORS-scoped to https://maps.abrarfarms.com already (v0.3.4). */
export const GEOSERVER_BASE = import.meta.env.VITE_GEOSERVER_BASE || 'https://geo.abrarfarms.com/geoserver';
export const GEOSERVER_WORKSPACE = import.meta.env.VITE_GEOSERVER_WORKSPACE || 'abrar';
export const WMS_URL = `${GEOSERVER_BASE}/${GEOSERVER_WORKSPACE}/wms`;
/** Just the host, for user-facing messages ("GeoServer (geo.abrarfarms.com)
 * isn't responding"). */
export const GEOSERVER_HOST = (() => {
  try {
    return new URL(GEOSERVER_BASE).host;
  } catch {
    return GEOSERVER_BASE;
  }
})();
export const WMTS_URL = `${GEOSERVER_BASE}/gwc/service/wmts`;

/** Real farm coordinate (lon, lat), same value used by the drone-processing
 * pipeline's location checks (pipeline/drone-processing/check_survey.py,
 * FARM_LON/FARM_LAT) — kept in sync manually since the two are different
 * runtimes (Python pipeline vs. this TS app). */
export const FARM_LON = 45.179728;
export const FARM_LAT = 2.225964;

/** EPSG:32638 — UTM zone 38N, the CRS every raster/vector in the `abrar`
 * workspace is published in (see docs/deployment.md, v0.3.4). */
export const FARM_EPSG = 'EPSG:32638';

/** Geotagged field-photo pins, sourced from the "Field Photos" Ente album via
 * pipeline/field-photos/publish_field_photos.py (see that script and
 * docs/deployment.md's v0.5.0 section for the full pipeline). In production
 * this reads a WFS GeoJSON response from the `abrar:field_photos` feature
 * type (same workspace/pattern as `abrar:survey_points`); in local dev, with
 * no live GeoServer to publish to, it falls back to a small committed test
 * fixture so the pin layer + detail panel can be visually verified — see
 * public/fixtures/field-photos.test.geojson (clearly marked as a fixture,
 * not real data). Override with VITE_FIELD_PHOTOS_URL either way. */
export const FIELD_PHOTOS_LAYER_NAME = 'field_photos';
export const FIELD_PHOTOS_URL =
  import.meta.env.VITE_FIELD_PHOTOS_URL ||
  (import.meta.env.DEV
    ? '/fixtures/field-photos.test.geojson'
    : `${GEOSERVER_BASE}/${GEOSERVER_WORKSPACE}/ows?service=WFS&version=2.0.0&request=GetFeature&typeName=${GEOSERVER_WORKSPACE}:${FIELD_PHOTOS_LAYER_NAME}&outputFormat=application/json`);
